/**
 * /invite/team — Team Invitation Preview + Accept/Decline
 *
 * Deep-link landing page for a teammate who received a team invite email.
 * The invite ACCEPT backend already exists (team-accept-invite edge
 * function / acceptTeamInvite() — src/lib/supabase/edge-functions.ts). This
 * screen is the client rail.
 *
 * Onboarding redesign Task 5 — this screen NO LONGER auto-accepts on mount.
 * It first fetches a READ-ONLY preview of the invite (previewTeamInvite → the
 * `action: 'preview'` branch of team-accept-invite: no membership insert, no
 * markAccepted) and renders the company / inviter / invited-email / assigned
 * status behind an explicit Accept / Decline choice. The invite is only
 * accepted when the user presses Accept — never as a side effect of loading
 * the page.
 *
 * Flow: checking-session → (redirect to /login if unauthenticated) →
 *   loading-preview → preview (Accept | Decline)
 *   Accept  → acceptTeamInvite → route to invited_member `personal` step
 *   Decline → declined acknowledgement (route to sign in)
 */

import { useEffect, useRef, useState } from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Home,
  Building2,
  Mail,
  UserPlus,
  BadgeCheck,
  LogIn,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import AnimatedPressable from '@/components/AnimatedPressable';
import { AppModal } from '@/components/modal';
import { ModalAction } from '@/components/modal/ModalAction';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useAuth } from '@/lib/supabase';
import {
  acceptTeamInvite,
  previewTeamInvite,
  type PreviewTeamInviteResponse,
} from '@/lib/supabase/edge-functions';
import { mode } from '@/lib/config/app-mode';
import { handleError, getUserMessage } from '@/lib/errors';
import { routeForStep, progressFor } from '@/lib/onboarding/machine';
import { FOUNDER_STATUS_OPTIONS } from '@/lib/onboarding/founder-status';
import { track, useTrackScreen } from '@/lib/analytics';
import { useEntrance } from '@/lib/a11y/reduced-motion';
import { myStartupKeys } from '@/lib/startup/use-my-startup';
import { resolveInvitePhase, canAcceptInvite, type AsyncState } from '@/lib/invite/invite-flow';

// ============================================================================
// Backend error classification
//
// team-accept-invite returns plain, already user-safe strings (see
// supabase/functions/team-accept-invite/index.ts). getUserMessage() alone
// collapses all of these to a generic "unexpected error" because none of
// them match its network/auth/validation keyword buckets — so we classify
// the known cases ourselves (same approach as src/app/claim/[token].tsx)
// and only fall back to getUserMessage() for genuinely unexpected errors.
// ============================================================================

type InviteErrorKind = 'expired' | 'wrong_email' | 'already_member' | 'invalid_token' | 'unknown';

function classifyInviteError(message: string): InviteErrorKind {
  const m = message.toLowerCase();
  if (m.includes('expired')) return 'expired';
  if (m.includes('different email')) return 'wrong_email';
  if (m.includes('already a member')) return 'already_member';
  if (m.includes('invalid') && m.includes('token')) return 'invalid_token';
  return 'unknown';
}

function inviteErrorCopy(kind: InviteErrorKind, fallbackMessage: string): { title: string; message: string; retryable: boolean } {
  switch (kind) {
    case 'expired':
      return {
        title: 'Invite Expired',
        message: 'This invite link has expired. Ask the team owner to send you a new invite.',
        retryable: false,
      };
    case 'wrong_email':
      return {
        title: 'Wrong Email Address',
        message: 'This invite was sent to a different email address. Sign in with the email the invite was sent to, then open the link again.',
        retryable: false,
      };
    case 'already_member':
      return {
        title: 'Already on a Team',
        message: 'You are already an active member of a team. Leave your current team before accepting a new invite.',
        retryable: false,
      };
    case 'invalid_token':
      return {
        title: 'Invalid Invite Link',
        message: 'This invite link is no longer valid. It may have already been used, or the link is incorrect.',
        retryable: false,
      };
    default:
      return {
        title: 'Something Went Wrong',
        message: fallbackMessage || 'An unexpected error occurred. Please try again.',
        retryable: true,
      };
  }
}

/** Human label for the invite's assigned founder status (null → conservative default). */
function founderStatusLabel(status: string | null): string {
  const match = FOUNDER_STATUS_OPTIONS.find((o) => o.value === status);
  return match?.label ?? 'Team member';
}

// ============================================================================
// Main Component
// ============================================================================

export default function InviteTeamScreen() {
  useTrackScreen('InviteTeam');
  const { token } = useLocalSearchParams<{ token: string }>();
  const router = useRouter();
  const colors = useThemeColors();
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();

  const [declined, setDeclined] = useState(false);
  const [declineModalVisible, setDeclineModalVisible] = useState(false);

  const hasAuth = !!user || mode.isMock;

  // Redirect unauthenticated visitors to sign in, preserving the invite link
  // via `return_to` (honored by src/app/login.tsx) so they land back here.
  // NOTE: navigation-in-render is deliberate and idempotent (router.replace) —
  // there is no side effect to gate; the preview query below stays disabled
  // until auth resolves, so no invite is ever read for an anonymous visitor.
  if (!authLoading && !hasAuth) {
    const returnTo = encodeURIComponent(`/invite/team?token=${token ?? ''}`);
    router.replace(`/login?return_to=${returnTo}`);
  }

  // READ-ONLY invite preview — resolves company/inviter/invited-email/status
  // WITHOUT accepting. This is the ONLY network call that fires on load; the
  // accept mutation below is wired exclusively to the Accept button.
  const previewQuery = useQuery({
    queryKey: ['invite', 'team', 'preview', token],
    queryFn: async (): Promise<PreviewTeamInviteResponse> => {
      const { data, error } = await previewTeamInvite({ token: token as string });
      if (error || !data) throw new Error(error || 'Failed to load invite.');
      return data;
    },
    enabled: hasAuth && !!token && !authLoading,
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await acceptTeamInvite({ token: token as string });
      if (error || !data) throw new Error(error || 'Failed to accept invite.');
      return data;
    },
    onSuccess: () => {
      // Activation §8 — invite accepted, after the awaited acceptTeamInvite
      // edge call resolved (the mutationFn throws on error, so onSuccess only
      // runs on a real server-confirmed accept).
      track('invite_accepted');
      // The invite-accept just created an active team_members row — the
      // cached "my startup" membership resolution (read by useMyStartup, e.g.
      // on the invitee personal step) is now stale. Invalidate so post-accept
      // UI reflects the new membership instead of the pre-accept result.
      queryClient.invalidateQueries({ queryKey: myStartupKeys.mine() });
      // Route into step 2 of the invited-member flow (the canonical machine's
      // invited_member → personal). Founder status was copied from the invite
      // onto the new active team_members row server-side; the personal step
      // reads it pre-set and read-only.
      router.replace(routeForStep('invited_member', 'personal'));
    },
    onError: (err) => {
      // team-accept-invite's expected business states (expired / wrong email /
      // already a member / invalid token) aren't bugs — don't alert Sentry for
      // them, only console-log. Anything else (network, 5xx, unexpected) still
      // reports normally so real failures surface.
      const rawMessage = err instanceof Error ? err.message : '';
      const kind = classifyInviteError(rawMessage);
      handleError(err, {
        context: 'InviteTeamScreen.acceptInvite',
        silent: kind !== 'unknown',
      });
    },
  });

  // Activation §8 — invite_viewed fires once when the READ-ONLY preview
  // actually resolves (data loaded), never on mount/redirect. This is the head
  // of the invited-member funnel.
  const inviteViewedTracked = useRef(false);
  useEffect(() => {
    if (inviteViewedTracked.current) return;
    if (previewQuery.isSuccess && previewQuery.data) {
      inviteViewedTracked.current = true;
      track('invite_viewed');
    }
  }, [previewQuery.isSuccess, previewQuery.data]);

  const acceptEnabled = canAcceptInvite({
    emailMatches: previewQuery.data?.emailMatches ?? false,
    accepting: acceptMutation.isPending,
  });

  const handleAccept = () => {
    // Explicit user action only — this is the SOLE caller of acceptMutation.
    if (!acceptEnabled) return;
    acceptMutation.mutate();
  };

  const handleConfirmDecline = () => {
    setDeclineModalVisible(false);
    setDeclined(true);
  };

  const handleGoToSignIn = () => {
    router.replace('/login');
  };

  const handleGoHome = () => {
    router.replace('/');
  };

  // ============================================================================
  // Render — a single pure phase resolver (invite-flow.ts) picks the screen.
  // Accept-derived phases are unreachable while the mutation is idle, so mount
  // can never accept; the screen rests on `preview` until the user acts.
  // ============================================================================

  const previewState: AsyncState = previewQuery.isLoading
    ? 'loading'
    : previewQuery.isError
    ? 'error'
    : previewQuery.data
    ? 'success'
    : 'idle';
  const acceptState: AsyncState = acceptMutation.isPending
    ? 'loading'
    : acceptMutation.isSuccess
    ? 'success'
    : acceptMutation.isError
    ? 'error'
    : 'idle';

  const phase = resolveInvitePhase({
    authResolved: !authLoading,
    hasAuth,
    hasToken: !!token,
    declined,
    acceptState,
    previewState,
    previewHasData: !!previewQuery.data,
  });

  switch (phase) {
    case 'checking-session':
      // Still resolving the session, or about to redirect to /login.
      return <StatusScreen colors={colors} message="Checking your session..." />;

    case 'missing-token':
      return (
        <ErrorScreen
          colors={colors}
          title="Invalid Invite Link"
          message="This invite link is missing its token. Please use the link from your invite email."
          retryable={false}
          onGoHome={handleGoHome}
        />
      );

    case 'declined':
      // User explicitly declined — acknowledge and route them to sign in.
      return <DeclinedScreen colors={colors} onSignIn={handleGoToSignIn} onGoHome={handleGoHome} />;

    case 'accepted':
      // Brief success while we route into the invited-member flow.
      return <StatusScreen colors={colors} success message="You're in! Taking you to onboarding..." />;

    case 'accept-error': {
      const rawMessage = acceptMutation.error instanceof Error ? acceptMutation.error.message : '';
      const kind = classifyInviteError(rawMessage);
      const copy = inviteErrorCopy(kind, getUserMessage(acceptMutation.error));
      return (
        <ErrorScreen
          colors={colors}
          title={copy.title}
          message={copy.message}
          retryable={copy.retryable}
          onRetry={copy.retryable ? handleAccept : undefined}
          onGoHome={handleGoHome}
        />
      );
    }

    case 'accepting':
      return <StatusScreen colors={colors} message="Joining team..." />;

    case 'loading-preview':
      return <StatusScreen colors={colors} message="Loading your invitation..." />;

    case 'preview-error': {
      const rawMessage = previewQuery.error instanceof Error ? previewQuery.error.message : '';
      const kind = classifyInviteError(rawMessage);
      const copy = inviteErrorCopy(kind, getUserMessage(previewQuery.error));
      return (
        <ErrorScreen
          colors={colors}
          title={copy.title}
          message={copy.message}
          retryable={copy.retryable}
          onRetry={copy.retryable ? () => previewQuery.refetch() : undefined}
          onGoHome={handleGoHome}
        />
      );
    }
  }

  // ── Preview: explicit Accept / Decline ────────────────────────────────────
  const preview = previewQuery.data!;
  const progress = progressFor('invited_member', 'invite'); // { index: 0, total: 2 }

  return (
    <>
      <PreviewScreen
        colors={colors}
        progress={progress}
        preview={preview}
        acceptEnabled={acceptEnabled}
        onAccept={handleAccept}
        onDecline={() => setDeclineModalVisible(true)}
      />

      <AppModal
        visible={declineModalVisible}
        onClose={() => setDeclineModalVisible(false)}
        title="Decline this invitation?"
        maxWidth={440}
        footer={
          <AppModal.Actions>
            <ModalAction
              label="Keep invitation"
              variant="neutral"
              onPress={() => setDeclineModalVisible(false)}
            />
            <ModalAction
              label="Decline"
              variant="destructive"
              onPress={handleConfirmDecline}
            />
          </AppModal.Actions>
        }
      >
        <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
          {preview.companyName
            ? `You won't join ${preview.companyName}. `
            : "You won't join this team. "}
          You can accept later using the same link while it's still valid, or ask the team owner to send a new one.
        </Text>
      </AppModal>
    </>
  );
}

// ============================================================================
// Sub-screens
// ============================================================================

function PreviewScreen({
  colors,
  progress,
  preview,
  acceptEnabled,
  onAccept,
  onDecline,
}: {
  colors: ReturnType<typeof useThemeColors>;
  progress: { index: number; total: number };
  preview: PreviewTeamInviteResponse;
  acceptEnabled: boolean;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const entrance = useEntrance();
  const companyName = preview.companyName || 'a team on Inverio';
  const inviterLabel = preview.inviterName || 'A team owner';
  const statusLabel = founderStatusLabel(preview.founderStatus);

  return (
    <SafeAreaView edges={['top', 'bottom']} className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <View className="flex-1 px-6 justify-center">
        <Animated.View
          entering={entrance(FadeInDown.delay(100).duration(500))}
          style={{ width: '100%', maxWidth: isWeb ? 480 : undefined, alignSelf: 'center' }}
        >
          {/* Machine-driven progress — invited-member is a 2-step flow (invite → personal). */}
          <View className="flex-row items-center gap-2 mb-6">
            {Array.from({ length: progress.total }).map((_, i) => (
              <View
                key={`invite-progress-${i}`}
                className="h-1 flex-1 rounded-full"
                style={{
                  backgroundColor: i <= progress.index ? colors.primary[500] : colors.border.light,
                  maxWidth: 48,
                }}
              />
            ))}
            <Text style={{ color: colors.text.tertiary }} className="text-xs font-medium ml-1" numberOfLines={1}>
              Step {progress.index + 1} of {progress.total}
            </Text>
          </View>

          {/* Header */}
          <View
            className="w-16 h-16 rounded-2xl items-center justify-center mb-5"
            style={{ backgroundColor: colors.primary[50] }}
          >
            <UserPlus size={30} color={colors.primary[600]} />
          </View>
          <Text className="text-2xl font-bold mb-2" style={{ color: colors.text.primary }} numberOfLines={2}>
            You&apos;re invited to join {companyName}
          </Text>
          <Text className="text-base mb-6" style={{ color: colors.text.secondary, lineHeight: 22 }} numberOfLines={3}>
            {inviterLabel} invited you to collaborate on Inverio. Review the details below, then accept to set up your profile.
          </Text>

          {/* Invitation detail card */}
          <View
            className="rounded-2xl p-4 mb-6"
            style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light, gap: 14 }}
          >
            <DetailRow
              colors={colors}
              icon={<Building2 size={18} color={colors.primary[600]} />}
              label="Company"
              value={companyName}
            />
            <DetailRow
              colors={colors}
              icon={<Mail size={18} color={colors.primary[600]} />}
              label="Invited email"
              value={preview.invitedEmail}
            />
            <DetailRow
              colors={colors}
              icon={<BadgeCheck size={18} color={colors.primary[600]} />}
              label="Your role"
              value={statusLabel}
            />
          </View>

          {/* Email-mismatch guard — Accept is disabled until they sign in as the invited email. */}
          {!preview.emailMatches && (
            <View
              className="flex-row items-start gap-2 p-3 rounded-xl mb-5"
              style={{ backgroundColor: colors.traffic.redLight }}
            >
              <AlertCircle size={18} color={colors.error} style={{ marginTop: 1 }} />
              <Text style={{ color: colors.error, flex: 1 }} className="text-sm" numberOfLines={3}>
                This invite was sent to {preview.invitedEmail}. Sign in with that email to accept it.
              </Text>
            </View>
          )}

          {/* Actions */}
          <Animated.View entering={entrance(FadeInUp.delay(200).duration(500))} style={{ gap: 10 }}>
            <View style={{ height: 52, borderRadius: 14, overflow: 'hidden', backgroundColor: acceptEnabled ? colors.primary[600] : colors.border.medium }}>
              <AnimatedPressable
                onPress={onAccept}
                disabled={!acceptEnabled}
                accessibilityRole="button"
                accessibilityLabel="Accept invitation"
                style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                <CheckCircle2 size={20} color={colors.text.inverse} />
                <Text className="font-semibold text-base" style={{ color: colors.text.inverse }} numberOfLines={1}>
                  Accept &amp; Continue
                </Text>
              </AnimatedPressable>
            </View>
            <View style={{ height: 52, borderRadius: 14, overflow: 'hidden', backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.medium }}>
              <AnimatedPressable
                onPress={onDecline}
                accessibilityRole="button"
                accessibilityLabel="Decline invitation"
                style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
              >
                <Text className="font-semibold text-base" style={{ color: colors.text.secondary }} numberOfLines={1}>
                  Decline
                </Text>
              </AnimatedPressable>
            </View>
          </Animated.View>
        </Animated.View>
      </View>
    </SafeAreaView>
  );
}

function DetailRow({
  colors,
  icon,
  label,
  value,
}: {
  colors: ReturnType<typeof useThemeColors>;
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <View className="flex-row items-center gap-3">
      <View
        className="w-9 h-9 rounded-lg items-center justify-center"
        style={{ backgroundColor: colors.primary[50] }}
      >
        {icon}
      </View>
      <View className="flex-1">
        <Text style={{ color: colors.text.tertiary }} className="text-xs font-medium mb-0.5" numberOfLines={1}>
          {label}
        </Text>
        <Text style={{ color: colors.text.primary }} className="text-sm font-semibold" numberOfLines={1}>
          {value}
        </Text>
      </View>
    </View>
  );
}

function DeclinedScreen({
  colors,
  onSignIn,
  onGoHome,
}: {
  colors: ReturnType<typeof useThemeColors>;
  onSignIn: () => void;
  onGoHome: () => void;
}) {
  const entrance = useEntrance();
  return (
    <SafeAreaView edges={['top', 'bottom']} className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <View className="flex-1 items-center justify-center px-6">
        <Animated.View
          entering={entrance(FadeInDown.delay(100).duration(500))}
          style={{ alignItems: 'center', maxWidth: isWeb ? 440 : undefined, width: '100%' }}
        >
          <View
            className="w-20 h-20 rounded-full items-center justify-center mb-6"
            style={{ backgroundColor: colors.surface.primary }}
          >
            <Home size={36} color={colors.text.tertiary} />
          </View>
          <Text className="text-2xl font-bold text-center mb-3" style={{ color: colors.text.primary }} numberOfLines={2}>
            Invitation declined
          </Text>
          <Text className="text-base text-center mb-8" style={{ color: colors.text.secondary, lineHeight: 22 }}>
            No problem. You can accept later from the same link while it&apos;s still valid, or ask the team owner to send a new one.
          </Text>
        </Animated.View>

        <Animated.View entering={entrance(FadeInUp.delay(200).duration(500))} style={{ width: '100%', maxWidth: isWeb ? 440 : undefined, gap: 10 }}>
          <View style={{ height: 52, borderRadius: 14, overflow: 'hidden', backgroundColor: colors.primary[600] }}>
            <AnimatedPressable
              onPress={onSignIn}
              accessibilityRole="button"
              accessibilityLabel="Go to sign in"
              style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              <LogIn size={18} color={colors.text.inverse} />
              <Text className="font-semibold text-base" style={{ color: colors.text.inverse }} numberOfLines={1}>
                Go to Sign In
              </Text>
            </AnimatedPressable>
          </View>
          <View style={{ height: 52, borderRadius: 14, overflow: 'hidden', backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.medium }}>
            <AnimatedPressable
              onPress={onGoHome}
              accessibilityRole="button"
              accessibilityLabel="Go to home page"
              style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
            >
              <Text className="font-semibold text-base" style={{ color: colors.text.secondary }} numberOfLines={1}>
                Go to Home
              </Text>
            </AnimatedPressable>
          </View>
        </Animated.View>
      </View>
    </SafeAreaView>
  );
}

function StatusScreen({
  colors,
  message,
  success = false,
}: {
  colors: ReturnType<typeof useThemeColors>;
  message: string;
  success?: boolean;
}) {
  const entrance = useEntrance();
  return (
    <SafeAreaView edges={['top', 'bottom']} className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <View className="flex-1 items-center justify-center px-6">
        <Animated.View entering={entrance(FadeInDown.delay(100).duration(500))} style={{ alignItems: 'center' }}>
          {success ? (
            <View
              className="w-20 h-20 rounded-full items-center justify-center mb-6"
              style={{ backgroundColor: `${colors.success}15` }}
            >
              <CheckCircle2 size={40} color={colors.success} />
            </View>
          ) : (
            <View
              className="w-20 h-20 rounded-full items-center justify-center mb-6"
              style={{ backgroundColor: `${colors.primary[500]}15` }}
            >
              <ActivityIndicator size="large" color={colors.primary[500]} />
            </View>
          )}
          <Text
            className="text-base font-medium text-center"
            style={{ color: colors.text.secondary, maxWidth: isWeb ? 420 : undefined }}
            numberOfLines={2}
          >
            {message}
          </Text>
        </Animated.View>
      </View>
    </SafeAreaView>
  );
}

function ErrorScreen({
  colors,
  title,
  message,
  retryable,
  onRetry,
  onGoHome,
}: {
  colors: ReturnType<typeof useThemeColors>;
  title: string;
  message: string;
  retryable: boolean;
  onRetry?: () => void;
  onGoHome: () => void;
}) {
  const entrance = useEntrance();
  return (
    <SafeAreaView edges={['top', 'bottom']} className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <View className="flex-1 items-center justify-center px-6">
        <Animated.View
          entering={entrance(FadeInDown.delay(100).duration(500))}
          style={{ alignItems: 'center', maxWidth: isWeb ? 440 : undefined, width: '100%' }}
        >
          <View
            className="w-20 h-20 rounded-full items-center justify-center mb-6"
            style={{ backgroundColor: `${colors.error}15` }}
          >
            <AlertCircle size={40} color={colors.error} />
          </View>
          <Text
            className="text-2xl font-bold text-center mb-3"
            style={{ color: colors.text.primary }}
            numberOfLines={2}
          >
            {title}
          </Text>
          <Text
            className="text-base text-center mb-8"
            style={{ color: colors.text.secondary, lineHeight: 22 }}
          >
            {message}
          </Text>
        </Animated.View>

        <Animated.View entering={entrance(FadeInUp.delay(200).duration(500))} style={{ width: '100%', maxWidth: isWeb ? 440 : undefined, gap: 10 }}>
          {retryable && onRetry && (
            <AnimatedPressable
              onPress={onRetry}
              className="rounded-xl py-4 flex-row items-center justify-center gap-2"
              style={{ backgroundColor: colors.primary[600] }}
              accessibilityRole="button"
              accessibilityLabel="Try again"
            >
              <RefreshCw size={18} color={colors.text.inverse} />
              <Text className="font-semibold text-base" style={{ color: colors.text.inverse }} numberOfLines={1}>
                Try Again
              </Text>
            </AnimatedPressable>
          )}
          <AnimatedPressable
            onPress={onGoHome}
            className="rounded-xl py-4 flex-row items-center justify-center gap-2"
            style={{
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.medium,
            }}
            accessibilityRole="button"
            accessibilityLabel="Go to home page"
          >
            <Home size={18} color={colors.text.secondary} />
            <Text className="font-semibold text-base" style={{ color: colors.text.secondary }} numberOfLines={1}>
              Go to Home
            </Text>
          </AnimatedPressable>
        </Animated.View>
      </View>
    </SafeAreaView>
  );
}
